import React, { useEffect, useRef, useState } from "react"
import { MessageModal, SectionHeading, TableCustom, WrapperAdvancedSearch } from "../../components/common"
import { useLista } from "../../components/hooks/useLista"
import { APIS } from "../../config/apis"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { ServerRequest } from "../../utils/apiweb"
import { useManagedContext } from "../../components/hooks"
import ConvenioDefinicionesModal from "../../components/controls/ConvenioDefinicionesModal"

import './index.scss';
import {usePermissions} from "../../components/hooks/usePermissions";
import {PERMISSIONS} from "../../consts/permissions";

const ConvenioDefinicionesView = () => {
    const { setIsLoading } = useManagedContext()

    //#region state management & lifecycle hooks
    const [data, setData] = useState([])
    const [editItem, setEditItem] = useState()
    const [editMode, setEditMode] = useState()
    const [removingId, setRemovingId] = useState(null)
    const [parametros, setParametros] = useState([])
    const [tableVisible, setTableVisible] = useState(false)

    const searchRef = useRef()

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.PLAN_PAGO_DEFINICION_EDIT);

    useEffect(() => {
        fetchParametros()
    }, [])
    //#endregion

    //#region requests
    const onNoSuccess = response => response.json()
        .then((error) => {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
            setIsLoading(false)
            setEditMode(null)
            setEditItem(null)
        })
        .catch((error) => {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
            setIsLoading(false)
            setEditMode(null)
            setEditItem(null)
    })

    const onError = error => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message)
        setIsLoading(false)
        setEditMode(null)
        setEditItem(null)
    }

    const fetchParametros = () => {
        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CONVENIO_PARAMETRO,
            null,
            null,
            res => res.json().then(data => setParametros(data)),
            onNoSuccess,
            onError,
        )
    }

    const SearchConvenios = () => {
        const filters = searchRef.current.filters
        const paramsUrl = `/filter?`
            + `codigo=${encodeURIComponent(filters.codigo)}`
            + `&convenio=${encodeURIComponent(filters.convenio)}`
            + `&nombre=${encodeURIComponent(filters.nombre)}`
            + `&idTipoTributo=${encodeURIComponent(filters.idTipoTributo)}`

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.TIPO_PLAN_PAGO,
            paramsUrl,
            null,
            res => res.json().then(data => {
                setData(data)
                setIsLoading(false)
                setTableVisible(true)
            }),
            onNoSuccess,
            onError,
        )
    }

    const [_, getRowLista, listaReady] = useLista({
        listas: ['TipoTributo'],
        onLoaded: (listas, isSuccess, error) => {
          if (!isSuccess) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'ConvenioDefinicionesTipoTributo',
          timeout: 0
        },
    })
    //#endregion

    //#region events
    const onAdd = () => {
        setEditMode('add')
        setEditItem({
            id: -Date.now(),
            codigo: '',
            nombre: '',
            idTipoTributo: 0,
            convenio: '',
            condiciones: '',
            firmas: '',
            firmasIzquierda: '',
            firmasDerecha: '',
        })
    }

    const onView = (item) => {
        setEditMode('view')
        setEditItem(item)
    }

    const onModify = (item) => {
        setEditMode('modify')
        setEditItem(item)
    }

    const confirmRemoveId = (id) => {
        setIsLoading(true)
        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.TIPO_PLAN_PAGO,
            `/${id}`,
            null,
            () => {
                setData(prev => prev.filter(x => x.id !== id))
                setIsLoading(false)
            },
            onNoSuccess,
            onError
        )
        setRemovingId(null)
    }

    const onModalConfirm = () => {
        setEditMode(null)
        setEditItem(null)
        if (tableVisible) SearchConvenios()
    }

    const onCancelEdit = () => {
        setEditMode(null)
        setEditItem(null)
    }
    //#endregion

    const cellAVMR = {
        id:'abm', accessor: 'id', width: '10%', disableGlobalFilter: true, disableSortBy: true,
        Header: () => !canEdit ? '' : (
            <div className='action'>
                <div onClick={onAdd} className="link">
                    <span className="material-symbols-outlined" title="Nuevo">add</span>
                </div>
            </div>
        ),
        Cell: (props) =>  (
            <div className='action'>
                <div onClick={() => onView(props.row.original)} className="link">
                    <span className="material-symbols-outlined" title="Ver">search</span>
                </div>
                {canEdit && (
                    <>
                        <div onClick={() => onModify(props.row.original)} className="link">
                            <span className="material-symbols-outlined" title="Modificar">stylus</span>
                        </div>
                        <div onClick={() => setRemovingId(props.row.original.id)} className="link">
                            <span className="material-symbols-outlined" title="Eliminar">delete</span>
                        </div>
                    </>
                )}
            </div>
        ),
    }

    const buildTipoTributoCell = ({ value }) => {
        if (!listaReady) return ''
        if (value === 0) return 'Todos los Tributos'
        else return getRowLista('TipoTributo', value)?.nombre ?? ''
    }

    return <>
        <SectionHeading titles={[{ title: 'Financiación' }, { title: 'Definición de convenios' }]}
                        onAddClick={canEdit ? onAdd : null} tableVisible={tableVisible} />

        <section className='section-accordion'>

            <WrapperAdvancedSearch
                ref={searchRef}
                filters={[
                    { title: 'Código', field: 'codigo', type: 'string', size: 3 },
                    { title: 'Nombre', field: 'nombre', type: 'string', size: 9 },
                    { title: 'Convenio', field: 'convenio', type: 'string', size: 3 },
                    { title: 'Tipo de tributo', field: 'idTipoTributo', type: 'list', lista: 'TipoTributo', size: 3 }
                ]}
                onSearch={SearchConvenios}
            />

            {tableVisible && (
                <TableCustom
                    className={'TableCustomBase m-top-20'}
                    data={data}
                    messageEmpty="No se encontraron resultados"
                    columns={[
                        { Header: 'Código', accessor: 'codigo', width: '20%', },
                        { Header: 'Nombre', accessor: 'nombre', width: '20%', },
                        { Header: 'Convenio', accessor: 'convenio', width: '20%', },
                        { Header: 'Tipo de tributo', accessor: 'idTipoTributo', Cell: buildTipoTributoCell, width: '20%', },
                        cellAVMR,
                    ]}
                />
            )}

            {editItem && (
                <ConvenioDefinicionesModal
                    parametros={parametros}
                    convenio={editItem}
                    editMode={editMode}
                    onConfirm={onModalConfirm}
                    onCancel={onCancelEdit}
                />
            )}

            {removingId && 
                <MessageModal
                    title={"Confirmación"}
                    message={"¿Está seguro de borrar el registro?"}
                    onDismiss={() => setRemovingId(null)}
                    onConfirm={() => confirmRemoveId(removingId)}
                />
            }
        </section>
    </>
}

export default ConvenioDefinicionesView
